import React, { useMemo, useState, useCallback } from 'react';
import { Calendar, Views, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, parseISO, addMonths, addDays, addWeeks, subDays, subWeeks, subMonths } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Shift, ShiftType, VacationDay } from '../../types/schedule';
import CalendarEmptyState from './CalendarEmptyState';

// Create proper localizer using date-fns
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// CSS to be added to your global styles or component
const calendarStyles = `
.rbc-calendar {
  min-height: 600px;
  border-radius: 8px;
  overflow: hidden;
}

.rbc-event {
  border-radius: 4px;
  padding: 4px;
}

.rbc-event.day-shift {
  background-color: #9B73BC;
}

.rbc-event.night-shift {
  background-color: #5E3A7C;
}

.rbc-event.vacation-day {
  background-color: #F97066;
}

.rbc-day-slot .rbc-event {
  border: none;
}

.rbc-today {
  background-color: #F8F7FA;
}

/* Fix for multi-day events in month view */
.rbc-month-view .rbc-event {
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

.rbc-month-view .rbc-event.night-shift {
  width: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

/* Ensure event content stays visible */
.rbc-event-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Span multi-day events properly */
.rbc-month-view .rbc-event-continues-prior {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.rbc-month-view .rbc-event-continues-after {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* Hide default toolbar */
.rbc-toolbar {
  display: none !important;
}
`;

interface ScheduleCalendarProps {
  shifts: Array<Shift & { provider_name?: string }>;
  onSelectShift?: (shift: Shift & { provider_name?: string }) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date; action: string }) => void;
  isEditable?: boolean;
  onCreateShifts?: () => void;
  vacationDays?: VacationDay[];
  teamMembers?: Array<{ user_id: string; full_name?: string }>;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  shiftType?: ShiftType;
  providerId: string | null;
  providerName?: string;
  resource?: Shift & { provider_name?: string };
  isVacationDay?: boolean;
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  shifts,
  onSelectShift,
  onSelectSlot,
  isEditable = false,
  onCreateShifts,
  vacationDays = [],
  teamMembers = [],
}) => {
  // Add state for the calendar date and view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.WEEK);

  // Get provider name from ID
  const getProviderName = useCallback((providerId: string) => {
    const provider = teamMembers.find(member => member.user_id === providerId);
    return provider?.full_name || providerId;
  }, [teamMembers]);

  // Convert shifts to calendar events
  const shiftEvents: CalendarEvent[] = useMemo(() => {
    return shifts.map((shift) => {
      const shiftDate = new Date(shift.date);
      
      // Create different start and end times based on shift type
      let start: Date, end: Date;
      
      if (shift.shift_type === 'day') {
        // Day shift: 7 AM to 7 PM
        start = new Date(shiftDate);
        start.setHours(7, 0, 0);
        
        end = new Date(shiftDate);
        end.setHours(19, 0, 0);
      } else {
        // Night shift: 7 PM to 7 AM the next day
        start = new Date(shiftDate);
        start.setHours(19, 0, 0);
        
        end = new Date(shiftDate);
        end.setDate(end.getDate() + 1);
        end.setHours(7, 0, 0);
      }
      
      // Determine title based on whether a provider is assigned
      const title = shift.provider_id
        ? `${shift.shift_type === 'day' ? 'Day' : 'Night'}: ${shift.provider_name || 'Assigned'}`
        : `${shift.shift_type === 'day' ? 'Day' : 'Night'}: Unassigned`;
        
      return {
        id: shift.id,
        title,
        start,
        end,
        shiftType: shift.shift_type as ShiftType,
        providerId: shift.provider_id,
        providerName: shift.provider_name,
        resource: shift,
        isVacationDay: false,
      };
    });
  }, [shifts]);

  // Convert vacation days to calendar events
  const vacationEvents: CalendarEvent[] = useMemo(() => {
    return vacationDays.map((vacationDay, index) => {
      try {
        // Parse the date string
        const vacationDate = parseISO(vacationDay.date);
        
        // Create an all-day event (from 00:00 to 23:59)
        const start = new Date(vacationDate);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(vacationDate);
        end.setHours(23, 59, 59, 999);
        
        // Get provider name
        const providerName = getProviderName(vacationDay.providerId);
        
        return {
          id: `vacation-${vacationDay.providerId}-${vacationDay.date}-${index}`,
          title: `Vacation: ${providerName}`,
          start,
          end,
          providerId: vacationDay.providerId,
          providerName,
          isVacationDay: true,
        };
      } catch (error) {
        console.error('Error parsing vacation day:', error);
        return null;
      }
    }).filter(Boolean) as CalendarEvent[];
  }, [vacationDays, getProviderName]);

  // Combine shifts and vacation days
  const events = useMemo(() => {
    return [...shiftEvents, ...vacationEvents];
  }, [shiftEvents, vacationEvents]);

  // Custom event styling
  const eventPropGetter = (event: CalendarEvent) => {
    if (event.isVacationDay) {
      return {
        className: 'vacation-day',
        style: {
          opacity: 0.9,
        },
      };
    }
    
    const className = event.shiftType === 'day' ? 'day-shift' : 'night-shift';
    
    return {
      className,
      style: {
        opacity: event.providerId ? 1 : 0.7,
      },
    };
  };

  // Handle event selection
  const handleSelectEvent = (event: CalendarEvent) => {
    if (!event.isVacationDay && onSelectShift && event.resource) {
      onSelectShift(event.resource);
    }
  };

  // Handle slot selection (for creating new shifts)
  const handleSelectSlot = (slotInfo: { start: Date; end: Date; action: string }) => {
    if (isEditable && onSelectSlot) {
      onSelectSlot(slotInfo);
    }
  };

  // Navigation handlers
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPrev = () => {
    if (currentView === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else if (currentView === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (currentView === 'day') {
      setCurrentDate(subDays(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (currentView === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (currentView === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (currentView === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const setView = (view: View) => {
    setCurrentView(view);
  };

  // Format the current date range based on the view
  const getDateRangeLabel = () => {
    if (currentView === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (currentView === 'week') {
      const start = currentDate;
      const end = addDays(start, 6);
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM d, yyyy');
    }
  };

  // Show empty state when there are no shifts and no vacation days
  if ((!shifts || shifts.length === 0) && (!vacationDays || vacationDays.length === 0)) {
    return (
      <CalendarEmptyState
        actionText={isEditable ? "Create Shifts" : undefined}
        onAction={isEditable && onCreateShifts ? onCreateShifts : undefined}
      />
    );
  }

  return (
    <div>
      <style>{calendarStyles}</style>
      
      {/* Custom navigation controls */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button 
            type="button" 
            onClick={goToToday}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            Today
          </button>
          <button 
            type="button" 
            onClick={goToPrev}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            Back
          </button>
          <button 
            type="button" 
            onClick={goToNext}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          >
            Next
          </button>
        </div>
        
        <span className="text-base font-medium">
          {getDateRangeLabel()}
        </span>
        
        <div className="flex border border-gray-300 rounded-md overflow-hidden">
          <button 
            type="button" 
            onClick={() => setView('month')}
            className={`px-4 py-2 text-sm focus:outline-none ${
              currentView === 'month' 
                ? 'bg-primary text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Month
          </button>
          <button 
            type="button" 
            onClick={() => setView('week')}
            className={`px-4 py-2 text-sm focus:outline-none ${
              currentView === 'week' 
                ? 'bg-primary text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Week
          </button>
          <button 
            type="button" 
            onClick={() => setView('day')}
            className={`px-4 py-2 text-sm focus:outline-none ${
              currentView === 'day' 
                ? 'bg-primary text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Day
          </button>
        </div>
      </div>
      
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        views={['month', 'week', 'day']}
        view={currentView}
        date={currentDate}
        onNavigate={() => {}} // We handle navigation ourselves
        onView={() => {}} // We handle view changes ourselves
        selectable={isEditable}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        eventPropGetter={eventPropGetter}
        formats={{
          dayHeaderFormat: (date: Date) => format(date, 'EEEE, MMMM d'),
          dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
            `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`,
          timeGutterFormat: (date: Date) => format(date, 'h a'),
        }}
        dayLayoutAlgorithm="no-overlap"
        popup
        min={new Date(0, 0, 0, 6, 0, 0)} // 6:00 AM
        max={new Date(0, 0, 0, 23, 0, 0)} // 11:00 PM
        showMultiDayTimes={true}
        allDayAccessor={() => false} // Disable all-day events area
        toolbar={false} // Hide the toolbar completely
      />
    </div>
  );
};

export default ScheduleCalendar; 